import React, { useMemo, useState } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../../common/CommonLoader/CommonLoader";
import AppButton from "../../../common/AppButton";
import {
  useBillingPeriods,
  useCreateBillingPeriod
} from "../../../../hooks/api/education/useAcademicStructure";

const BillingPeriodList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const {
    data: years = [],
    isPending: yearsLoading,
    refetch: refetchYears
  } = useBillingPeriods({ type: "ACADEMIC_YEAR" });
  const { data: terms = [], isPending: termsLoading } = useBillingPeriods({
    type: "TERM"
  });
  const createYear = useCreateBillingPeriod();
  const [yearLabel, setYearLabel] = useState("");

  const termCountByYear = useMemo(() => {
    const counts = new Map<string, number>();
    terms.forEach((term) => {
      if (term.parentId) {
        counts.set(term.parentId, (counts.get(term.parentId) ?? 0) + 1);
      }
    });
    return counts;
  }, [terms]);

  if (yearsLoading || termsLoading) {
    return (
      <CommonLoader
        loadingMessage={t(
          "loading-billing-periods",
          "Loading billing periods..."
        )}
      />
    );
  }

  return (
    <Box data-testid="billing-period-list-page" sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {t("billing-periods-title", "Billing periods")}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <TextField
          size="small"
          fullWidth
          label={t("academic-year-label", "Academic year label")}
          value={yearLabel}
          onChange={(event) => setYearLabel(event.target.value)}
        />
        <AppButton
          disabled={!yearLabel.trim() || createYear.isPending}
          onClick={async () => {
            await createYear.mutateAsync({
              type: "ACADEMIC_YEAR",
              label: yearLabel.trim()
            });
            setYearLabel("");
            refetchYears();
          }}
        >
          {t("add-year", "Add year")}
        </AppButton>
      </Stack>

      {years.length === 0 ? (
        <Typography color="text.secondary">
          {t("billing-years-empty", "No academic years yet.")}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {years.map((year) => (
            <Box
              component="li"
              key={year.id}
              sx={{
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between"
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {year.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("term-count", "{{count}} terms", {
                    count: termCountByYear.get(year.id) ?? 0
                  })}
                </Typography>
              </Box>
              <AppButton variant="text" onClick={() => navigate(year.id)}>
                {t("view", "View")}
              </AppButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default BillingPeriodList;
